import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useMyActions } from "../context/MyActionsContext";
import BackBar from "./BackBar";
import { NotificationBell } from "./NotificationBell";
import {
  House, QrCode, Truck, FileText, ShieldCheck, Wrench, Users, Package,
  ClipboardText, WarningOctagon, Database, GearSix, ListChecks, SignOut, Books,
  ShieldWarning, GraduationCap, HardHat, SealCheck, ChartBar, TreeStructure, Buildings, CaretDown, Vibrate,
  ClockCounterClockwise, ShieldPlus, MagnifyingGlass, Barbell, UserCircle, BookOpen, Plus, HandArrowDown,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Home", Icon: House, show: () => true, mobile: true, section: "main" },
  { to: "/my-profile", label: "My Profile", Icon: UserCircle, show: () => true, section: "main" },
  { to: "/scan", label: "Scan", Icon: QrCode, show: () => true, mobile: true, section: "main" },
  { to: "/assets", label: "Assets", Icon: Truck, show: () => true, mobile: true, section: "main" },
  { to: "/assets?new=1", label: "Add Asset", Icon: Plus, show: () => true, section: "main" },
  { to: "/asset-transfers", label: "Custody Queue", Icon: HandArrowDown, show: (p) => p("asset_manager", "supervisor", "administrator"), section: "main" },
  { to: "/assets/tree", label: "Asset Tree", Icon: Package, show: (p) => p("supervisor", "administrator", "asset_manager", "auditor", "maintenance"), section: "main" },
  { to: "/my-equipment", label: "My Equipment", Icon: Package, show: () => true, section: "main" },
  { to: "/garage", label: "Garage / Stores", Icon: Truck, show: (p) => p("supervisor", "administrator", "asset_manager", "auditor"), section: "main" },
  { to: "/equipment-admin", label: "Equipment Manager", Icon: Package, show: (p) => p("supervisor", "administrator", "asset_manager", "manage_vehicle_loadout", "manage_towing_data", "manage_kit_contents"), section: "admin" },
  { to: "/documents", label: "Docs", Icon: Books, show: () => true, mobile: true, section: "compliance" },
  { to: "/policies", label: "Document Library", Icon: BookOpen, show: () => true, section: "compliance" },
  { to: "/policy-manager", label: "Policy Manager", Icon: ShieldPlus, show: (p) => p("supervisor", "administrator", "rams_manager"), section: "compliance" },
  { to: "/policy-matrix", label: "Training Matrix (Docs)", Icon: SealCheck, show: (p) => p("supervisor", "administrator", "rams_manager", "training_manager", "auditor"), section: "compliance" },
  { to: "/policies?class=rams", label: "RAMS / Method Statements", Icon: ShieldWarning, show: () => true, section: "compliance" },
  { to: "/my-rams", label: "My RAMS / MS", Icon: ShieldWarning, show: () => true, section: "compliance" },
  { to: "/ppe", label: "My PPE", Icon: HardHat, show: () => true, section: "compliance" },
  { to: "/training", label: "Training", Icon: GraduationCap, show: (p) => p("driver", "maintenance", "supervisor", "administrator", "training_manager", "auditor"), section: "compliance" },
  { to: "/training/pending", label: "Pending Approvals", Icon: SealCheck, show: (p) => p("training_manager", "administrator", "verify_competencies"), section: "compliance" },
  { to: "/compliance", label: "Compliance", Icon: SealCheck, show: (p) => p("supervisor", "administrator", "auditor", "training_manager"), section: "compliance" },
  { to: "/havs", label: "HAVS", Icon: Vibrate, show: (p) => p("driver", "maintenance", "supervisor", "administrator", "havs_manager", "health_admin"), section: "compliance" },
  { to: "/loler", label: "LOLER", Icon: Barbell, show: (p) => p("supervisor", "administrator", "auditor", "asset_manager", "loler_view", "loler_admin"), section: "compliance" },
  { to: "/reports", label: "Reports", Icon: ChartBar, show: (p) => p("supervisor", "administrator", "auditor", "training_manager", "ppe_manager", "rams_manager"), section: "compliance" },
  { to: "/maintenance", label: "Maintenance", Icon: Wrench, show: (p) => p("maintenance", "supervisor", "administrator"), section: "maintenance" },
  { to: "/maintenance/part-orders", label: "Part Orders", Icon: Package, show: (p) => p("maintenance", "supervisor", "administrator"), section: "maintenance" },
  { to: "/faults", label: "Faults", Icon: WarningOctagon, show: (p) => p("maintenance", "asset_manager", "supervisor", "administrator"), section: "maintenance" },
  { to: "/maintenance/history", label: "Service History", Icon: ClockCounterClockwise, show: (p) => p("maintenance", "asset_manager", "supervisor", "administrator", "auditor"), section: "maintenance" },
  { to: "/maintenance/parts", label: "Parts & Stock", Icon: Package, show: (p) => p("maintenance", "asset_manager", "supervisor", "administrator"), section: "maintenance" },
  { to: "/insurance", label: "Insurance", Icon: ShieldPlus, show: (p) => p("asset_manager", "supervisor", "administrator", "auditor"), section: "admin" },
  { to: "/quick-data", label: "Quick Data", Icon: Database, show: (p) => p("maintenance", "supervisor", "administrator"), section: "admin" },
  { to: "/checks", label: "Checks", Icon: ClipboardText, show: (p) => p("supervisor", "administrator"), section: "admin" },
  { to: "/people", label: "People", Icon: Users, show: (p) => p("supervisor", "administrator", "auditor"), section: "admin" },
  { to: "/departments", label: "Departments", Icon: TreeStructure, show: (p) => p("supervisor", "administrator"), section: "admin" },
  { to: "/audit", label: "Audit", Icon: ListChecks, show: (p) => p("administrator", "auditor", "supervisor"), section: "admin" },
  { to: "/admin", label: "Admin", Icon: GearSix, show: (p) => p("administrator"), section: "admin" },
];

const SECTIONS = [
  { key: "main", label: null },
  { key: "compliance", label: "Compliance", Icon: SealCheck },
  { key: "maintenance", label: "Maintenance", Icon: Wrench },
  { key: "admin", label: "Admin", Icon: GearSix },
];

export default function Layout({ children }) {
  const { profile, hasPerm, logout, viewAs, exitViewAs, organisations, currentOrg, isPlatformAdmin, switchOrg } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => n.show(hasPerm));
  const mobileItems = items.filter((n) => n.mobile).slice(0, 4).concat([{ to: "/more", label: "More", Icon: GearSix, mobile: true }]);
  const orgName = organisations.find((o) => o.id === currentOrg?.id)?.name || currentOrg?.name;

  const { data: myActions } = useMyActions();
  const attn = myActions
    ? (myActions.rts_required?.length || 0) + (myActions.rams_outstanding?.length || 0) + (myActions.ppe_attention?.length || 0) + (myActions.training_attention?.length || 0)
    : 0;
  const [badges, setBadges] = useState({});

  const location = useLocation();
  const isActivePath = (to) => to === "/" ? location.pathname === "/" : location.pathname === to || location.pathname.startsWith(to + "/");
  const navKey = profile?.id ? `tmic_nav_${profile.id}` : "tmic_nav";
  const [openSections, setOpenSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem(navKey) || "{}"); } catch { return {}; }
  });
  // Load this user's saved layout once their profile resolves
  useEffect(() => {
    try { const s = JSON.parse(localStorage.getItem(`tmic_nav_${profile?.id}`) || "null"); if (s) setOpenSections(s); } catch { /* ignore */ }
  }, [profile?.id]);
  // Persist the layout whenever it changes
  useEffect(() => {
    try { localStorage.setItem(navKey, JSON.stringify(openSections)); } catch { /* ignore */ }
  }, [openSections, navKey]);
  // Auto-expand the section that owns the current route
  useEffect(() => {
    const active = SECTIONS.find((s) => s.label && items.some((n) => n.section === s.key && isActivePath(n.to)));
    if (active) setOpenSections((o) => ({ ...o, [active.key]: true }));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const [navQuery, setNavQuery] = useState("");
  useEffect(() => { setNavQuery(""); }, [location.pathname]);
  useEffect(() => { api.get("/nav-badges").then((r) => setBadges(r.data || {})).catch(() => {}); }, [location.pathname]);
  const searchResults = navQuery ? items.filter((n) => n.label.toLowerCase().includes(navQuery.toLowerCase())) : [];

  const renderLink = (n, indented) => (
    <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 pr-5 py-2.5 font-body text-sm font-medium transition-colors duration-100 ${indented ? "pl-8" : "pl-5"} ${
          isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
      <n.Icon size={19} weight="bold" /> <span className="flex-1">{n.label}</span>
      {n.to === "/" && attn > 0 && <span data-testid="nav-attention-badge" className="bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">{attn}</span>}
    </NavLink>
  );

  const doLogout = async () => { await logout(); nav("/login"); };
  const doExit = async () => { await exitViewAs(); nav("/"); };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-60 bg-slate-900 text-slate-200 fixed h-full">
        <div className="p-5 border-b border-slate-700">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-display text-2xl font-black text-white tracking-tight leading-none">TMIC OPS</div>
              <div className="font-body text-xs text-slate-400 mt-1">Operations & Compliance</div>
            </div>
            <NotificationBell testid="notification-bell" />
          </div>
        </div>
        {/* Organisation context / switcher */}
        {orgName && (
          <div className="px-5 py-3 border-b border-slate-700">
            {organisations.length > 1 ? (
              <div className="relative group">
                <button data-testid="org-switcher" className="w-full flex items-center justify-between gap-2 text-left">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-white truncate"><Buildings size={16} weight="bold" />{orgName}</span>
                  <CaretDown size={14} weight="bold" className="text-slate-400" />
                </button>
                <div className="absolute z-50 left-0 right-0 mt-1 bg-slate-800 rounded shadow-lg hidden group-hover:block">
                  {organisations.map((o) => (
                    <button key={o.id} data-testid={`org-option-${o.id}`} onClick={() => switchOrg(o.id)}
                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-slate-700 ${o.id === currentOrg?.id ? "text-white font-semibold" : "text-slate-300"}`}>{o.name}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 truncate" data-testid="org-name"><Buildings size={16} weight="bold" />{orgName}</div>
            )}
          </div>
        )}
        <nav className="flex-1 overflow-y-auto py-3">
          <div className="px-3 pb-2">
            <div className="relative">
              <MagnifyingGlass size={15} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="nav-search" value={navQuery} onChange={(e) => setNavQuery(e.target.value)} placeholder="Jump to a page…"
                className="w-full h-9 pl-8 pr-2 rounded bg-slate-800 text-sm text-slate-100 placeholder:text-slate-500 border border-slate-700 focus:border-blue-500 outline-none" />
            </div>
          </div>
          {navQuery ? (
            <div data-testid="nav-search-results">
              {searchResults.length === 0
                ? <p className="px-5 py-3 font-body text-xs text-slate-500">No pages match “{navQuery}”.</p>
                : searchResults.map((n) => renderLink(n, false))}
            </div>
          ) : SECTIONS.map((sec) => {
            const secItems = items.filter((n) => n.section === sec.key);
            if (secItems.length === 0) return null;
            if (!sec.label) return <div key={sec.key} className="mb-1">{secItems.map((n) => renderLink(n, false))}</div>;
            const expanded = !!openSections[sec.key];
            const sectionAttn = secItems.some((n) => isActivePath(n.to));
            const secBadge = sec.key === "maintenance" ? (badges.open_faults || 0) : sec.key === "compliance" ? (badges.checks_overdue || 0) : 0;
            return (
              <div key={sec.key} className="mt-1.5">
                <button data-testid={`nav-section-${sec.key}`} onClick={() => setOpenSections((o) => ({ ...o, [sec.key]: !expanded }))}
                  className={`w-full flex items-center gap-2 px-5 py-2 font-body text-[10px] font-black uppercase tracking-widest transition-colors duration-150 ${
                    sectionAttn && !expanded ? "text-white" : "text-slate-400 hover:text-slate-100"}`}>
                  <sec.Icon size={13} weight="bold" className="opacity-70" />
                  <span className="flex-1 text-left">{sec.label}</span>
                  {secBadge > 0 && <span data-testid={`nav-section-badge-${sec.key}`} role="link" title={sec.key === "maintenance" ? "View open faults" : "View checks"}
                    onClick={(e) => { e.stopPropagation(); nav(sec.key === "maintenance" ? "/faults" : "/checks/overdue"); }}
                    className="cursor-pointer bg-red-600 hover:bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center normal-case tracking-normal">{secBadge}</span>}
                  <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
                </button>
                <div className={`grid transition-all duration-200 ease-out ${expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden" aria-hidden={!expanded} inert={!expanded || undefined}>{secItems.map((n) => renderLink(n, true))}</div>
                </div>
              </div>
            );
          })}
          {isPlatformAdmin && !navQuery && (
            <NavLink to="/platform" data-testid="nav-platform"
              className={({ isActive }) => `flex items-center gap-3 px-5 py-3 font-body text-sm font-medium border-t border-slate-700 mt-2 ${isActive ? "bg-purple-600 text-white" : "text-purple-300 hover:bg-slate-800"}`}>
              <Buildings size={20} weight="bold" /> Platform Admin
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-slate-700">
          <NavLink to="/my-profile" data-testid="nav-my-profile-footer" className="block hover:opacity-80 transition-opacity">
            <div className="font-body text-sm text-white font-semibold truncate">{profile?.first_name} {profile?.last_name}</div>
            <div className="font-body text-xs text-slate-400 truncate mb-2">{profile?.email}</div>
          </NavLink>
          <button data-testid="logout-button" onClick={doLogout}
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <SignOut size={18} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 md:ml-60 pb-20 md:pb-0">
        {viewAs && (
          <div data-testid="view-as-banner" className="bg-amber-500 text-slate-900 px-4 py-2 flex items-center justify-between sticky top-0 z-50 font-body text-sm font-bold">
            <span className="inline-flex items-center gap-2"><ShieldWarning size={18} weight="fill" /> VIEWING AS {(viewAs.name || "USER").toUpperCase()} — READ ONLY</span>
            <button data-testid="exit-view-as" onClick={doExit} className="underline underline-offset-2 hover:text-slate-700">Exit View As</button>
          </div>
        )}
        <header className="md:hidden bg-slate-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <span className="font-display text-xl font-black tracking-tight">TMIC OPS</span>
          <div className="flex items-center gap-3">
            <NotificationBell testid="notification-bell-mobile" />
            <button data-testid="logout-button-mobile" onClick={doLogout}><SignOut size={22} weight="bold" /></button>
          </div>
        </header>
        <main className="p-4 sm:p-6 md:p-8 max-w-7xl w-full min-w-0"><BackBar />{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t-2 border-slate-300 flex z-40">
        {mobileItems.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`mobilenav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-2 gap-0.5 ${isActive ? "text-blue-600" : "text-slate-500"}`}>
            <div className="relative"><n.Icon size={22} weight="bold" />{n.to === "/" && attn > 0 && <span data-testid="mobilenav-attention-badge" className="absolute -top-1.5 -right-2 bg-red-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{attn}</span>}</div>
            <span className="text-[10px] font-semibold">{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
